<script setup lang="ts">
import { routes } from '@app/router/routes'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'

// Inline "Complete KYC" blocker that replaces a financial-action region
// (Add funds / Withdraw / Add bank) while the investor's KYC is pending
// verification. Parents render this conditionally — it does not self-gate — so
// the same widget drops into any action surface. Presentational only:
// navigation is a RouterLink to the dashboard, where the re-KYC card lives.
interface Props {
  title?: string
  description?: string
}

withDefaults(defineProps<Props>(), {
  title: 'Verification pending',
  description:
    'This action is unavailable until your identity is verified. Complete your KYC to start transacting — you can keep browsing in the meantime.',
})
</script>

<template>
  <Box direction="col" align="center" gap="3" class="kyc-blocker">
    <Box align="center" justify="center" class="kyc-blocker__icon">
      <PiIcon name="clock" />
    </Box>
    <Box direction="col" gap="1" align="center">
      <Text type="subheading" weight="semibold" align="center">{{ title }}</Text>
      <Text type="body-sm" color="muted" align="center">{{ description }}</Text>
    </Box>
    <RouterLink :to="routes.DASHBOARD" class="kyc-blocker__cta">
      <Text type="body-sm" weight="semibold" :color="'var(--p-warning-700)'">Complete KYC</Text>
    </RouterLink>
  </Box>
</template>

<style scoped>
.kyc-blocker {
  width: 100%;
  box-sizing: border-box;
  padding: 1.25rem;
  background: var(--p-warning-50);
  border: 1px solid var(--p-warning-200);
  border-radius: var(--p-border-radius-lg);
}

.kyc-blocker__icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  font-size: 1rem;
  background: var(--p-warning-100);
  color: var(--p-warning-700);
}

.kyc-blocker__cta {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border: 1px solid var(--p-warning-300);
  border-radius: var(--p-border-radius-md);
  text-decoration: none;
  background: var(--p-surface-0);
  transition: background 0.15s;
}

.kyc-blocker__cta:hover {
  background: var(--p-warning-100);
}
</style>
